import React, { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { API_URL } from "../utils/config.js";

const dash = "\u2014";
const cap = (n) => {
  if (n == null) return "\u2014";
  n = Number(n);
  if (n >= 1000) return "$" + (n / 1000).toFixed(2) + "T";
  if (n >= 1) return "$" + n.toFixed(2) + "B";
  return "$" + (n * 1000).toFixed(0) + "M";
};
const pct = (v) => {
  if (v == null) return dash;
  const n = Number(v);
  return <span className={n >= 0 ? "change-up" : "change-down"}>{n >= 0 ? "+" : ""}{n.toFixed(2)}%</span>;
};
const COLS = [["name", "Company"], ["price", "Price"], ["change", "1D"], ["ret1y", "1Y"], ["mcap", "Market cap"]];

export default function CompaniesBrowser({ sector, onSector }) {
  const [sectors, setSectors] = useState([]);
  const [data, setData] = useState({ total: 0, rows: [] });
  const [sort, setSort] = useState("mcap");
  const [dir, setDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [qq, setQq] = useState("");
  const [loading, setLoading] = useState(true);
  const limit = 50;

  useEffect(() => {
    fetch(API_URL + "/market/sectors").then((r) => r.json()).then(setSectors).catch(() => {});
  }, []);
  useEffect(() => {
    const t = setTimeout(() => { setQq(q.trim()); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => { setPage(1); }, [sector]);
  useEffect(() => {
    let off = false;
    setLoading(true);
    const u = API_URL + "/market/companies?sort=" + sort + "&dir=" + dir + "&page=" + page + "&limit=" + limit +
      (sector !== "all" ? "&sector=" + encodeURIComponent(sector) : "") + (qq ? "&q=" + encodeURIComponent(qq) : "");
    fetch(u).then((r) => r.json()).then((d) => { if (!off) { setData(d); setLoading(false); } })
      .catch(() => { if (!off) setLoading(false); });
    return () => { off = true; };
  }, [sector, sort, dir, page, qq]);

  const total = sectors.reduce((a, s) => a + s.n, 0);
  const pages = Math.max(1, Math.ceil((data.total || 0) / limit));
  const clickSort = (k) => {
    if (sort === k) setDir(dir === "asc" ? "desc" : "asc");
    else { setSort(k); setDir(k === "name" ? "asc" : "desc"); }
    setPage(1);
  };
  const arrow = (k) => (sort === k ? (dir === "asc" ? " \u25b2" : " \u25bc") : "");

  return (
    <section>
      <div className="cb-pills">
        <button className={"category-pill " + (sector === "all" ? "active" : "")} onClick={() => onSector("all")}>
          All <small>{total.toLocaleString()}</small>
        </button>
        {sectors.map((s) => (
          <button key={s.sector} className={"category-pill " + (sector === s.sector ? "active" : "")} onClick={() => onSector(s.sector)}>
            {s.sector} <small>{s.n.toLocaleString()}</small>
          </button>
        ))}
      </div>

      <div className="cb-bar">
        <h5 className="fw-bold mb-0">{sector === "all" ? "All companies" : sector} <span className="text-muted fw-normal small">({(data.total || 0).toLocaleString()})</span></h5>
        <div className="cb-search">
          <Search size={16} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or ticker" />
        </div>
      </div>

      <div className="card">
        <div className="table-responsive">
          <table className="table align-middle mb-0 companies-table" style={{ opacity: loading ? 0.6 : 1 }}>
            <thead>
              <tr>
                {COLS.map(([k, label]) => (
                  <th key={k} className={"cb-th" + (k === "name" ? "" : " text-end")} onClick={() => clickSort(k)}>{label}{arrow(k)}</th>
                ))}
                <th className="text-end">52W range</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => {
                const lo = Number(r.low_52w), hi = Number(r.high_52w), pr = Number(r.price);
                const pos = hi > lo && r.price != null ? Math.min(100, Math.max(0, ((pr - lo) / (hi - lo)) * 100)) : null;
                return (
                  <tr key={r.ticker}>
                    <td>
                      <a className="fw-bold wl-company" href={"#/stock/" + r.ticker}>{r.name}</a>
                      <div className="text-muted small">{r.ticker}{r.sector ? " \u00b7 " + r.sector : ""}</div>
                    </td>
                    <td className="text-end">{r.price == null ? dash : "$" + pr.toFixed(2)}</td>
                    <td className="text-end">{pct(r.change_1d)}</td>
                    <td className="text-end">{pct(r.ret_1y)}</td>
                    <td className="text-end">{cap(r.mcap)}</td>
                    <td className="text-end">
                      {pos == null ? dash : (
                        <div className="cb-range-wrap">
                          <span className="small text-muted">{lo.toFixed(2)}</span>
                          <div className="cb-range"><i style={{ left: pos + "%" }} /></div>
                          <span className="small text-muted">{hi.toFixed(2)}</span>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {!loading && data.rows.length === 0 && (
                <tr><td colSpan={6} className="text-center text-muted py-4">No companies found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="cb-pager">
        <button className="btn btn-outline-secondary btn-sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
        <span className="small text-muted">Page {page} of {pages}</span>
        <button className="btn btn-outline-secondary btn-sm" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</button>
      </div>
      <p className="text-muted small">Prices are delayed about 15 minutes. Sectors come from each company's SEC industry (SIC) code. Preferred shares, warrants and units are hidden.</p>
    </section>
  );
}